import DynamicModal from 'components/DynamicModal';
import IconButton from 'components/IconButton';
import { useAtomValue, useSetAtom } from 'jotai';
import callApi from 'lib/callApi';
import { showFailureToast, showSuccessToast } from 'lib/ToastHelper';
import React, { useState } from 'react';
import { CircleSpinner } from 'react-spinners-kit';
import { Button } from 'reactstrap';
import { getDrillDownUrl } from 'views/Admin/Dashboard/helper';
import { companyIdAtom } from 'views/state';

import history from '../../../../history.js';
import RepositoryDetailModal from '../RepositoryDetailModal';
import { forceRerenderUIAtom } from '../state';

const Actions = ({ rowData, onLoading }) => {
  const [modalOpen, setModalOpen] = useState(false);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const companyId = useAtomValue(companyIdAtom);
  const setForceRerenderUI = useSetAtom(forceRerenderUIAtom);

  const [loading, setLoading] = useState(false);

  const rowId = rowData['id'];
  const isActive = rowData['is_active'];
  const isAccountActive = rowData['account']['is_active'];

  const handleActivateDeactivateRepository = async (isActive: boolean) => {
    const data = {
      company_id: companyId,
      is_active: isActive,
    };
    setLoading(true);
    onLoading(true);
    callApi(`sast/repositories/activation/${rowData?.id}/`, 'PATCH', {}, data)
      .then((res) => {
        if (res.status === 200) {
          rowData['is_active'] = isActive;
          rowData['status'] = 'Updating...';
          setForceRerenderUI(Math.random().toString());
          showSuccessToast(
            `Repository is successfully ${
              isActive ? 'activated' : 'deactivated'
            }.`,
          );
        }
      })
      .catch((err) => {
        console.error(`Failed to de/activate repository:`, err);
        showFailureToast(
          `Failed ${isActive ? 'activate' : 'deactivate'} repository. ${
            err?.statusText
          }: ${err?.data?.error}`,
        );
      })
      .finally(() => {
        setLoading(false);
        onLoading(false);
      });
  };

  const repositoryDetailModal = () => {
    return (
      <RepositoryDetailModal
        repoId={rowId}
        isOpen={modalOpen}
        toggle={() => setModalOpen(!modalOpen)}
        isActive={isActive}
        isAccountActive={isAccountActive}
        handleActivateDeactivateRepository={handleActivateDeactivateRepository}
      />
    );
  };

  const confirmModal = () => {
    return (
      <DynamicModal
        isOpen={confirmModalOpen}
        toggle={() => setConfirmModalOpen(!confirmModalOpen)}
        title={`Confirm Manual Scan`}
        content={
          <div>
            <p>
              No new commit detected on repository branch. Are you sure you want
              to proceed with scan?
            </p>
          </div>
        }
        size="sm"
        footerActions={
          <>
            <Button
              color="secondary"
              onClick={() => setConfirmModalOpen(!confirmModalOpen)}
            >
              Cancel
            </Button>
            <Button
              color="danger"
              disabled={loading}
              onClick={() => {
                setConfirmModalOpen(!confirmModalOpen);
                handleManualScan(true);
              }}
            >
              Confirm
            </Button>
          </>
        }
      />
    );
  };

  const manualScanSpinner = (
    <CircleSpinner size={20} color="#0665a9" loading={true} />
  );

  const handleManualScan = async (forceLaunch = false) => {
    const lastCommitDate = rowData?.last_commit_date;
    const lastScannedAt = rowData?.scan?.last_sync_completed_at;
    console.log('manual scan', lastCommitDate, lastScannedAt);

    // if scanned after last commit date, get a confirmation for re-scanning
    if (
      !forceLaunch &&
      lastCommitDate &&
      lastScannedAt &&
      lastScannedAt > lastCommitDate
    ) {
      setConfirmModalOpen(true);
      return;
    }

    setLoading(true);
    const data = {
      company_id: companyId,
    };

    callApi(`sast/repositories/scan/${rowData?.id}/`, 'PATCH', {}, data)
      .then((res) => {
        if (res.status === 201) {
          rowData['scan_in_progress'] = true;
          rowData['status'] = 'Updating...';
          setForceRerenderUI(Math.random().toString());
          showSuccessToast('Manual scan initiated successfully.');
        } else if (res.status === 200) {
          setConfirmModalOpen(!confirmModalOpen);
        }
      })
      .catch((err) => {
        console.error('Failed to start manual scan:', err);
        if (err?.status === 405) {
          showFailureToast(
            `Failed to start manual scan. ${err?.statusText}: ${err?.data?.message}`,
          );
          setForceRerenderUI(Math.random().toString());
        } else {
          showFailureToast(
            `Failed to start manual scan. ${err?.statusText}: ${err?.data?.error}`,
          );
        }
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const getActiveIconToolTip = () => {
    let tooltip = isActive
      ? 'Deactivate scanning for this repository'
      : 'Activate scanning for this repository';

    if (!isAccountActive) {
      tooltip = 'The repository account is inactive.';
    }

    return tooltip;
  };

  return (
    <div className="text-nowrap">
      {/* <IconButton id="scansLink" size='sm'
          onClick={() => { }}
          iconElement={manualScanSpinner}
        /> */}
      <IconButton
        id={`toggle_${rowId}`}
        iconClass={`fa-solid ${isActive ? 'fa-toggle-on' : 'fa-toggle-off'}`}
        size="sm"
        disabled={!isAccountActive}
        tooltip={getActiveIconToolTip()}
        onClick={() => handleActivateDeactivateRepository(!isActive)}
      />
      {rowData['scan_in_progress'] || loading ? (
        <IconButton
          id="scansLink"
          size="sm"
          tooltip={`Scan Satus: ${rowData['status']}`}
          onClick={() => {}}
          iconElement={manualScanSpinner}
        />
      ) : (
        <IconButton
          id={`scanLink_${rowData['id']}`}
          iconClass="fa fa-search"
          size="sm"
          tooltip={
            rowData['is_active']
              ? 'Click to initiate scan'
              : 'Inactive repositories cannot be scanned'
          }
          onClick={() => handleManualScan()}
          disabled={!isActive || !isAccountActive}
        />
      )}
      <IconButton
        id="vulnLink"
        iconClass="fa-solid fa-bug"
        size="sm"
        tooltip={
          rowData['is_active']
            ? 'View Vulnerabilities'
            : 'Action disabled for inactive repository'
        }
        disabled={!isActive || !isAccountActive}
        onClick={() =>
          history.push(
            getDrillDownUrl({
              type: 'open',
              sourceType: 'sast2',
              hosts_in: [rowData.id],
              severity: '',
              issueType: 'VULNERABILITY',
              qid: '',
              severity_in: undefined,
            }),
            // '/' +
            //   atob(localStorage.getItem(btoa('approle')) ?? '') +
            //   `/qualys/vulnerabilities?type=open&source_type=sast2&issue_type=VULNERABILITY&sast_id=${rowData['id']}`,
          )
        }
      />
      <IconButton
        id="bugsLink"
        iconClass="fa-solid fa-bugs"
        size="sm"
        tooltip={
          rowData['is_active']
            ? 'View Bugs'
            : 'Action disabled for inactive repository'
        }
        disabled={!isActive || !isAccountActive}
        onClick={() =>
          history.push(
            getDrillDownUrl({
              type: 'open',
              sourceType: 'sast2',
              hosts_in: [rowData.id],
              severity: '',
              issueType: 'BUG',
              qid: '',
              severity_in: undefined,
            }),
            // '/' +
            //   atob(localStorage.getItem(btoa('approle')) ?? '') +
            //   `/qualys/vulnerabilities?type=open&source_type=sast2&issue_type=BUG&sast_id=${rowData['id']}`,
          )
        }
      />
      <IconButton
        id="disabledetailsLink"
        iconClass="fa fa-eye"
        size="sm"
        tooltip="View Repository/Branch Details"
        onClick={() => setModalOpen(!modalOpen)}
      />
      {repositoryDetailModal()}
      {confirmModal()}
    </div>
  );
};

export default Actions;
